import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/DBContext';
import BasicInfoStep from './BasicInfoStep';
import AccountTypeStep from './AccountTypeStep';
import ProfileDetailsStep from './ProfileDetailsStep';
import firebaseErrorMessages from '../../firebaseError';

const SignupWizard = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    postcode: '',
    localAuthorityRep: null,
    accountType: '',
    profile: {},
    children: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for errors
    if (name === 'errors') {
      setErrors(value);
      return;
    }

    // Handle regular form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
    setErrors({});
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signUp(formData);
      navigate("/emailverification");
    } catch (error) {
      if (error.message.includes("ZERO_RESULTS")) {
        setErrors({
          general: 'Invalid address or postcode. Please check and try again.'
        });
      } else {
        const firebaseErrorCode = error.code;
        const customErrorMessage = firebaseErrorMessages[firebaseErrorCode] || error.message;
        setErrors({ general: customErrorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep 
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
            errors={errors}
          />
        );
      case 2:
        return (
          <AccountTypeStep
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
            prevStep={prevStep}
            errors={errors}
          />
        );
      case 3:
        return (
          <ProfileDetailsStep
            formData={formData}
            handleInputChange={handleInputChange}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
            errors={errors}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="relative">
        <div className="h-2 bg-gray-200 rounded-full">
          <div 
            className="h-2 bg-blue-600 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 3) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-2">
          <span className={`text-xs ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-500'}`}>Basic Info</span>
          <span className={`text-xs ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-500'}`}>Account Type</span>
          <span className={`text-xs ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-500'}`}>Profile Details</span>
        </div>
      </div>

      {errors.general && (
        <div className="text-center text-red-600 text-sm">
          {errors.general}
        </div>
      )}

      {renderStep()}
    </div>
  );
};

export default SignupWizard; 